import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type ReminderRule, type SaveReminderRuleRequest } from '../api/client'
import { invalidateReminders } from '../api/invalidate'
import './ReminderEditor.css'

/** The lead time asked for, and the default everywhere. */
const DEFAULT_DAYS_BEFORE = 30

/**
 * One vehicle's reminder for one obligation.
 *
 * ONE component, used from the Reminders tab and from the bell on each
 * Compliance & provenance row. Two forms over one row of data would drift — the
 * bell would grow a field the tab lacks, or validate differently — and the drift
 * only ever shows up as a rule that behaves unlike the one beside it.
 *
 * It edits a `ReminderRule` with `vehicleId` set. The fleet-wide shapes on
 * `/reminders` are the same table and the same service; what makes this one
 * per-vehicle is that single column.
 */
export function ReminderEditor({
  vehicleId,
  registration,
  complianceTypeId,
  obligationName,
  existing,
  onClose,
}: {
  vehicleId: number
  registration: string
  complianceTypeId: number
  obligationName: string
  /** The rule being edited, or undefined to create one. */
  existing?: ReminderRule
  onClose: () => void
}) {
  const queryClient = useQueryClient()

  const [name, setName] = useState(
    existing?.name ?? `${obligationName} — ${registration}`,
  )
  const [daysBefore, setDaysBefore] = useState(existing?.daysBefore ?? DEFAULT_DAYS_BEFORE)
  const [remindOnDate, setRemindOnDate] = useState(existing?.remindOnDate ?? '')
  const [to, setTo] = useState(existing?.toAddresses ?? '')
  const [cc, setCc] = useState(existing?.ccAddresses ?? '')
  const [notes, setNotes] = useState(existing?.notes ?? '')
  const [subject, setSubject] = useState(existing?.subjectTemplate ?? '')
  const [body, setBody] = useState(existing?.bodyTemplate ?? '')
  const [invite, setInvite] = useState(existing?.sendCalendarInvite ?? false)
  const [enabled, setEnabled] = useState(existing?.isEnabled ?? true)
  const [error, setError] = useState<string | null>(null)

  /*
    What this rule would send today, shown READ-ONLY beside the boxes.

    ⚠️ It is deliberately NOT copied into the fields. An earlier version seeded
    the body box with the composed HTML, and that was wrong twice over: it put a
    wall of markup in front of somebody who wanted to type a sentence, and — far
    worse — saving it froze that snapshot as the rule's template for ever. A rule
    previewed on a quiet day would have been frozen with "0 obligations" and an
    empty table, and gone on sending that after the fleet changed underneath it.

    Empty means "compose it", which is what almost every rule should do: a
    composed message counts what is actually due each time it runs.
  */
  const preview = useQuery({
    queryKey: ['reminder-preview', existing?.reminderRuleId],
    queryFn: () => api.previewReminder(existing!.reminderRuleId!),
    enabled: existing?.reminderRuleId != null,
  })

  const [showPreview, setShowPreview] = useState(false)

  const save = useMutation({
    mutationFn: (request: SaveReminderRuleRequest) => api.saveReminderRule(request),
    onSuccess: async (result) => {
      await invalidateReminders(queryClient)
      if (result.message) setError(null)
      onClose()
    },
    onError: (e: Error) => setError(e.message),
  })

  const remove = useMutation({
    mutationFn: () => api.deleteReminderRule(existing!.reminderRuleId!),
    onSuccess: async () => {
      await invalidateReminders(queryClient)
      onClose()
    },
    onError: (e: Error) => setError(e.message),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    save.mutate({
      reminderRuleId: existing?.reminderRuleId ?? null,
      name: name.trim(),
      complianceTypeId,
      // Never set beside a vehicle: a vehicle already has one site, so this
      // would be redundant at best and match nothing at worst. The API refuses
      // it, and this is the half that keeps the refusal from ever being needed.
      siteId: null,
      vehicleId,
      daysBefore,
      remindOnDate: remindOnDate || null,
      toAddresses: to.trim(),
      ccAddresses: cc.trim() || null,
      notes: notes.trim() || null,
      // Empty means "compose it", which is what most rules should do: a composed
      // subject stays right as the facts move, a stored one is a snapshot.
      subjectTemplate: subject.trim() || null,
      bodyTemplate: body.trim() || null,
      sendCalendarInvite: invite,
      includeUnknown: false,
      isEnabled: enabled,
      rowVersion: existing?.rowVersion ?? null,
    })
  }

  return (
    <form className="remed" onSubmit={submit}>
      <div className="remed__head">
        <h4>
          {existing ? 'Edit reminder' : 'New reminder'}
          <span className="remed__for">
            {obligationName} · {registration}
          </span>
        </h4>
        <button type="button" className="remed__close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      <label className="remed__field">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={128} />
      </label>

      <div className="remed__row">
        <label className="remed__field">
          <span>Warn this many days before</span>
          <input
            type="number"
            min={1}
            max={365}
            value={daysBefore}
            onChange={(e) => setDaysBefore(Number(e.target.value))}
            required
          />
        </label>

        <label className="remed__field">
          <span>
            Or start on
            <em>optional</em>
          </span>
          <input
            type="date"
            value={remindOnDate}
            onChange={(e) => setRemindOnDate(e.target.value)}
          />
        </label>
      </div>

      {/*
        Both, not either. The lead time is what decides whether an obligation is
        approaching; the date only decides when this rule starts speaking. Saying
        so here beats a reader assuming the date replaces the lead time and
        wondering why nothing arrived.
      */}
      <p className="remed__hint">
        {remindOnDate
          ? `Silent until ${remindOnDate}, then warns about anything within ${daysBefore} days of expiry.`
          : `Warns as soon as this obligation is within ${daysBefore} days of expiry.`}
      </p>

      <label className="remed__field">
        <span>To</span>
        <input
          type="text"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="fleet@example.com, depot@example.com"
          required
        />
      </label>

      <label className="remed__field">
        <span>
          Cc
          <em>optional</em>
        </span>
        <input
          type="text"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
          placeholder="finance@example.com"
        />
      </label>

      {/*
        Stated, not buried. This reverses how reminders used to behave — one
        message per recipient, so the list stayed private — and somebody typing
        addresses into a box deserves to know before they press save.
      */}
      <p className="remed__hint remed__hint--warn">
        Everyone on this message sees the whole To and Cc list.
      </p>

      <label className="remed__field">
        <span>
          Notes
          <em>optional</em>
        </span>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1000}
          placeholder="Book the depot slot first."
        />
      </label>

      <label className="remed__field">
        <span>
          Subject
          <em>leave blank to compose it</em>
        </span>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={400}
          // A placeholder, never a value: typing over it is a choice, and an
          // untouched box stays "compose it" rather than freezing today's
          // wording into the rule.
          placeholder={preview.data?.subject ?? 'Composed from the obligation and the count'}
        />
      </label>

      <label className="remed__field">
        <span>
          Message
          <em>leave blank to compose it</em>
        </span>
        <textarea
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={
            'Leave this empty and the message is written for you each time, ' +
            'counting whatever is actually due.\n\nOr type your own words here.'
          }
        />
      </label>

      {/*
        Written-out warning rather than a quiet note: a stored message stops
        counting. Somebody typing here is choosing a snapshot over a live figure,
        and should know that before they save rather than months later.
      */}
      {body.trim().length > 0 && (
        <p className="remed__hint remed__hint--warn">
          Your own words are sent exactly as written — the count and the list of
          vehicles will not update themselves. Use the placeholders below to keep
          the details current.
        </p>
      )}

      {/* What it would send today, to read rather than to edit. */}
      {existing && preview.data && (
        <div className="remed__preview">
          <button type="button" onClick={() => setShowPreview((v) => !v)}>
            {showPreview ? 'Hide' : 'Show'} what this would send today
          </button>
          {showPreview && (
            <div className="remed__previewbody">
              <p className="remed__previewsub">{preview.data.subject}</p>
              {/* Rendered, not as source. The point is to see the message. */}
              <div
                className="remed__previewhtml"
                // The API composed this from our own templates; it is not user
                // input arriving from anywhere else.
                dangerouslySetInnerHTML={{ __html: preview.data.html ?? '' }}
              />
              {preview.data.matchingCount === 0 && (
                <p className="remed__hint">
                  Nothing is due for this rule right now, so the preview is empty. It
                  will fill in when something falls inside the lead time.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <details className="remed__tokens">
        <summary>Placeholders you can use</summary>
        {/*
          Read from the same list the renderer substitutes, never a hand-kept
          copy. A help text maintained separately drifts, and the drift is only
          discovered by an operator whose reminder went out with a token in it.
        */}
        <p>
          {'{{Registration}} {{Obligation}} {{ExpiryDate}} {{DaysUntil}} {{DaysBefore}} '}
          {'{{SiteName}} {{Notes}} {{RuleName}} {{Count}} {{VehicleList}} {{AsOfDate}}'}
        </p>
      </details>

      <label className="remed__check">
        <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} />
        <span>
          Send a calendar invitation
          <em>
            Adds the expiry to the recipient&rsquo;s calendar. Whether it appears without
            being opened is their mailbox&rsquo;s setting, not ours.
          </em>
        </span>
      </label>

      <label className="remed__check">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>Active</span>
      </label>

      {error && <p className="remed__error">{error}</p>}

      <div className="remed__actions">
        {existing && (
          <button
            type="button"
            className="btn btn--danger"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            {remove.isPending ? 'Removing…' : 'Remove'}
          </button>
        )}
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn btn--primary" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save reminder'}
        </button>
      </div>
    </form>
  )
}
